/**
 * lib/examDuties.ts — who is on duty, and whether they can be.
 * ------------------------------------------------------------------
 * Pure, like examTimetable.ts beside it: the screens draw, this decides.
 *
 * THE MODEL IS THE OFFICE'S OWN. Read off their November 2026 spreadsheet
 * (89 papers, 250 duties, 33 staff) rather than invented:
 *
 *   keyTime      ONE person supervising candidates in the window around the
 *                paper — before it starts, or after a short paper finishes —
 *                because papers of different lengths share a session. Their
 *                window is not the paper's ("08:00–09:00", paper 09:00–11:00).
 *   invigilator  in the room for the paper itself. Often several, sometimes
 *                on split shifts ("09:30–11:00"), some marked (S).
 *
 * A duty carries its OWN window for that reason, defaulting to the paper's.
 *
 * FOUR RULES THE SCHOOL SET:
 *   1. A teacher may invigilate their own subject's paper — but never as the
 *      only invigilator in the room.
 *   2. A teacher timetabled to teach during the window is not offered; the
 *      exam officer may still choose them, and is told what it costs.
 *   3. A DUTY IS A SESSION, NOT A PAPER. Papers in one session run
 *      CONCURRENTLY IN THE SAME ROOM, so a teacher named on two of them is
 *      standing in one room once: it counts once, its hours are the UNION of
 *      the windows (13:00–15:30 covers 13:15–15:00), and it is never a
 *      double booking. Live, 20 teacher-sessions carry two papers.
 *   4. A SECONDARY EXAM IS STAFFED BY SECONDARY TEACHERS (2026-09-23). The
 *      picker offered all 74 teachers and 34 of them work in primary, so
 *      nearly half the list was people who have no business in an IGCSE hall.
 *      The office already knew: their own hand-built November 2026 rota has
 *      ZERO primary-only staff on it across 250 duties. See `phaseOfPaper`.
 *
 *   5. INTERNAL EXAMS (the school's own; the school, 2026-10-30):
 *      - there is no FCS / key-time supervision, so the screens offer and
 *        print invigilators only;
 *      - A FORM SITTING AN EXAM HAS NO LESSONS, so a lesson for that form at
 *        that time is not a clash: its teacher is free (`lessonsThatRun`);
 *      - heads of department do not invigilate (the screen leaves them off
 *        the list it suggests);
 *      - the lightest load comes first, as for cover: the fewest lessons and
 *        duties that day, then the fewest duties in the series (`dayLoad`).
 *
 * Rules 1 to 3 were set on 2026-09-18, rule 4 on 2026-09-23, rule 5 on
 * 2026-10-30.
 *
 * Rooms are deliberately absent: the office's own rota has no room column,
 * and rooms come with the seating phase.
 */
import { dayForDate, isSecondaryClassName, slotTeacherIds } from '@/lib/timetable';
import type { Day, TeacherPhase, TimetableSlot } from '@/lib/timetable';

/**
 * Primary or secondary — which part of the school something belongs to.
 *
 * Deliberately an ALIAS of `TeacherPhase` rather than a second union saying
 * the same words. The whole point is to compare a paper's phase with a
 * teacher's, and two unions that must always agree are two unions that can
 * drift.
 */
export type SchoolPhase = TeacherPhase;

export type DutyRole = 'keyTime' | 'invigilator';

export interface ExamDuty {
  teacherId: string;
  /** Stored, not looked up: a rota has to still read right when a teacher
   *  leaves and their record goes. */
  teacherName: string;
  role: DutyRole;
  /** 24-hour, Gaborone. Defaults to the paper's window on import. */
  from: string;
  to: string;
  /** The office's own "(S)" against a name. Kept verbatim because nobody has
   *  yet told us what it means, and dropping it would lose their intent. */
  marker?: string;
}

/** One paper in one session — exactly one row of the office's spreadsheet. */
export interface ExamPaperDuties {
  id: string;
  seriesId: string;
  date: string;
  /** LKC adds 'MM', its own exams' mid-morning session. */
  session: 'AM' | 'MM' | 'PM' | '';
  code: string;
  subject: string;
  component: string;
  duration: string;
  entries: number;
  start: string;
  end: string;
  duties: ExamDuty[];
  /** The form sitting it, on an INTERNAL paper (lib/internalExams). */
  form?: number;
}

/* ── clock arithmetic, in minutes past midnight ───────────────────── */

export function minutesOf(hhmm: string): number {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm ?? '').trim());
  return m ? Number(m[1]) * 60 + Number(m[2]) : NaN;
}

export function overlaps(aFrom: string, aTo: string, bFrom: string, bTo: string): boolean {
  const a1 = minutesOf(aFrom); const a2 = minutesOf(aTo);
  const b1 = minutesOf(bFrom); const b2 = minutesOf(bTo);
  if ([a1, a2, b1, b2].some(Number.isNaN)) return false;
  // Touching is not overlapping: a duty ending at 09:00 does not clash with a
  // lesson starting at 09:00, and the office writes shifts exactly that way.
  return a1 < b2 && b1 < a2;
}

export function dutyMinutes(duty: Pick<ExamDuty, 'from' | 'to'>): number {
  const from = minutesOf(duty.from); const to = minutesOf(duty.to);
  return Number.isNaN(from) || Number.isNaN(to) ? 0 : Math.max(0, to - from);
}

/** The timetable day an exam date falls on — LKC's cycle day (School
 *  Settings → Teaching Days), so the rota sees the lessons that date really
 *  has. Null when the date has no teaching. */
export function dayOf(isoDate: string): Day | null {
  const [y, m, d] = String(isoDate ?? '').split('-').map(Number);
  if (!y || !m || !d) return null;
  return dayForDate(String(isoDate));
}

/* ── what a duty would cost ───────────────────────────────────────── */

/** Period number → its clock window, from the school's own schedule. */
export interface PeriodTimes { period: number; from: string; to: string }

/**
 * The teaching periods a window covers.
 *
 * The schedule is per DAY because the school's days differ (Friday is short),
 * and the period numbers on a timetable slot mean nothing without it.
 */
export function periodsInWindow(times: PeriodTimes[], from: string, to: string): number[] {
  return times.filter((p) => overlaps(from, to, p.from, p.to)).map((p) => p.period);
}

export interface DutyClash {
  slot: TimetableSlot;
  periods: number[];
  /** What is being taught, read from the GROUP the teacher is in on a split
   *  slot — an option block's subject lives there, and the slot's own label
   *  is blank, which would leave the warning naming classes and nothing
   *  else ("Form 4A, Form 4B"). */
  subject: string;
}

/**
 * The lessons a teacher would miss by taking this duty.
 *
 * Returns the slots, not a boolean: "Mr Mazorodze teaches Form 3B Physics,
 * periods 2 and 3" is what lets the exam officer decide, and "unavailable"
 * is what makes them override it without reading.
 */
export function clashesFor(
  teacherId: string,
  window: { date: string; from: string; to: string },
  slots: TimetableSlot[],
  times: PeriodTimes[],
): DutyClash[] {
  const day = dayOf(window.date);
  if (!day || !teacherId) return [];
  const covered = new Set(periodsInWindow(times, window.from, window.to));
  if (!covered.size) return [];
  const out: DutyClash[] = [];
  for (const slot of slots) {
    if (slot.day !== day) continue;
    // slotTeacherIds, not slot.teacherIds: a combined-split slot (an option
    // block) carries its teachers inside `groups`, and reading the top-level
    // field alone tells a teacher of Form 4 options that they are free.
    if (!slotTeacherIds(slot).includes(teacherId)) continue;
    const periods: number[] = [];
    for (let p = slot.period_start; p <= slot.period_end; p += 1) if (covered.has(p)) periods.push(p);
    if (!periods.length) continue;
    const group = (slot.groups ?? []).find((g) => (g.teacherIds ?? []).includes(teacherId));
    out.push({ slot, periods, subject: group?.subjectLabel || slot.subjectLabel || '' });
  }
  return out;
}

/* ── lessons that do not run because the form is sitting an exam ─── */

/** The form of a timetable class name ("Form 4A" -> 4); null when it is not a form. */
export function formOfClassName(name: string): number | null {
  const m = /\bform\s*(\d)/i.exec(String(name ?? ''));
  return m ? Number(m[1]) : null;
}

/**
 * The slots that still run on `date` (rule 5). A lesson whose EVERY class is
 * in a form sitting an internal paper at that time does not happen, so it is
 * taken out before clashes are looked for and before the day's load is
 * counted. A lesson shared with a form that is not sitting still runs. Papers
 * with no `form` (Cambridge) cancel nothing: their candidates are a few
 * pupils out of a class, and the class is still taught.
 */
export function lessonsThatRun(
  slots: TimetableSlot[],
  papers: ExamPaperDuties[],
  date: string,
  times: PeriodTimes[],
): TimetableSlot[] {
  const sitting = papers.filter((p) => p.date === date && typeof p.form === 'number' && p.start && p.end);
  if (!sitting.length) return slots;
  const day = dayOf(date);
  return slots.filter((slot) => {
    if (slot.day !== day) return true;
    const forms = (slot.classes ?? []).map(formOfClassName);
    if (!forms.length || forms.some((f) => f == null)) return true;
    const at = times.filter((t) => t.period >= slot.period_start && t.period <= slot.period_end);
    if (!at.length) return true;
    const from = at[0].from;
    const to = at[at.length - 1].to;
    return !forms.every((f) => sitting.some((p) => p.form === f && overlaps(from, to, p.start, p.end)));
  });
}

/**
 * How busy a teacher already is on `date` (rule 5): the periods they teach
 * that still run, and the sessions they are on duty. The picker puts the
 * lightest day first, as cover does.
 */
export function dayLoad(
  teacherId: string,
  date: string,
  runningSlots: TimetableSlot[],
  times: PeriodTimes[],
  papers: ExamPaperDuties[],
): { lessons: number; duties: number } {
  const lessons = clashesFor(teacherId, { date, from: '00:00', to: '23:59' }, runningSlots, times)
    .reduce((n, c) => n + c.periods.length, 0);
  const sessions = new Set(papers
    .filter((p) => p.date === date && p.duties.some((d) => d.teacherId === teacherId))
    .map((p) => p.session));
  return { lessons, duties: sessions.size };
}

/* ── does this teacher teach what is being sat ────────────────────── */

/** Lower case, letters and digits only, one space between words. */
function words(text: string): string[] {
  return String(text ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .split(' ')
    .filter(Boolean);
}

/**
 * The subject a paper is IN, without the office's component tail.
 *
 * Their spreadsheet names a paper by subject and component in one cell —
 * "Physics - Practical", "Chemistry (Practical)", "Mathematics (Mechanics)",
 * "Physics - Exten." — so the tail after a dash or a bracket is dropped
 * before anything is compared with a teacher's subject.
 */
export function paperSubject(subject: string): string {
  return words(String(subject ?? '').split(/[-–(]/)[0]).join(' ');
}

/**
 * Whether a teacher's subject IS the subject of a paper.
 *
 * Matched on whole words at either end rather than by `includes`, because the
 * two names are written by different people and neither is canonical:
 * "Business Studies" is the school's name for the paper called "Business",
 * "Additional Mathematics" is a mathematician's paper, and — the case that
 * makes a substring test wrong — "English Language" and "Literature in
 * English" are different subjects sat by different pupils.
 */
export function subjectsMatch(teacherSubject: string, examSubject: string): boolean {
  const a = words(teacherSubject);
  const b = words(paperSubject(examSubject));
  if (!a.length || !b.length) return false;
  const [shorter, longer] = a.length <= b.length ? [a, b] : [b, a];
  const starts = shorter.every((w, i) => longer[i] === w);
  const offset = longer.length - shorter.length;
  const ends = shorter.every((w, i) => longer[offset + i] === w);
  return starts || ends;
}

/* ── a duty is a session ──────────────────────────────────────────── */

/** Overlapping or touching windows joined into continuous spans, in order.
 *  Touching counts as continuous here — a teacher who works 08:00–09:30 and
 *  09:30–11:00 never left the room. */
export function mergeWindows(windows: { from: string; to: string }[]): { from: string; to: string }[] {
  const valid = windows
    .filter((w) => !Number.isNaN(minutesOf(w.from)) && !Number.isNaN(minutesOf(w.to)))
    .sort((a, b) => minutesOf(a.from) - minutesOf(b.from));
  const out: { from: string; to: string }[] = [];
  for (const w of valid) {
    const last = out[out.length - 1];
    if (last && minutesOf(w.from) <= minutesOf(last.to)) {
      if (minutesOf(w.to) > minutesOf(last.to)) last.to = w.to;
    } else out.push({ ...w });
  }
  return out;
}

/** One teacher, one session: everything they are in that room for. */
export interface DutySession {
  key: string;
  date: string;
  session: string;
  /** Each paper they are named on, in the order they start. */
  entries: { paper: ExamPaperDuties; duty: ExamDuty }[];
  /** The windows they are actually present for, merged. */
  spans: { from: string; to: string }[];
  minutes: number;
  /** keyTime first where they hold both — it is the earlier, odder window. */
  roles: DutyRole[];
}

export function sessionDutiesFor(teacherId: string, papers: ExamPaperDuties[]): DutySession[] {
  const by = new Map<string, DutySession>();
  for (const { paper, duty } of dutiesFor(teacherId, papers)) {
    const key = `${paper.date}|${paper.session}`;
    const row = by.get(key)
      ?? { key, date: paper.date, session: paper.session, entries: [], spans: [], minutes: 0, roles: [] };
    row.entries.push({ paper, duty });
    by.set(key, row);
  }
  for (const row of by.values()) {
    row.entries.sort((a, b) => a.duty.from.localeCompare(b.duty.from)
      || a.paper.code.localeCompare(b.paper.code));
    row.spans = mergeWindows(row.entries.map((e) => ({ from: e.duty.from, to: e.duty.to })));
    row.minutes = row.spans.reduce((n, w) => n + dutyMinutes(w), 0);
    row.roles = (['keyTime', 'invigilator'] as DutyRole[])
      .filter((role) => row.entries.some((e) => e.duty.role === role));
  }
  return [...by.values()].sort((a, b) => a.date.localeCompare(b.date) || a.session.localeCompare(b.session));
}

/** A teacher's sessions laid out a day at a time, morning beside afternoon —
 *  which is the thing a rota has to say at a glance. A session the office
 *  left unlabelled is neither, and is handed back in `other` rather than
 *  being filed as a morning it may not be. */
export interface DutyDay {
  date: string;
  am: DutySession | null;
  /** LKC's mid-morning session; always null for a Cambridge series. */
  mm: DutySession | null;
  pm: DutySession | null;
  other: DutySession[];
}

export function dutyDays(sessions: DutySession[]): DutyDay[] {
  const by = new Map<string, DutyDay>();
  for (const s of sessions) {
    const day = by.get(s.date) ?? { date: s.date, am: null, mm: null, pm: null, other: [] };
    if (s.session === 'AM') day.am = s;
    else if (s.session === 'MM') day.mm = s;
    else if (s.session === 'PM') day.pm = s;
    else day.other.push(s);
    by.set(s.date, day);
  }
  return [...by.values()].sort((a, b) => a.date.localeCompare(b.date));
}

/* ── the load, and what is wrong with it ──────────────────────────── */

export interface DutyTally {
  teacherId: string;
  teacherName: string;
  duties: number;
  minutes: number;
  keyTime: number;
}

/**
 * Duties per teacher across a series, heaviest first — so the load can be
 * seen rather than argued about.
 *
 * COUNTED IN SESSIONS, not papers (rule 3 above). Two papers sat
 * concurrently in one room is one duty and one stretch of time; counting the
 * rows would have told Mr Dube he had done two afternoons on 28 September
 * and charged him five hours for two and a half.
 */
export function dutyTally(papers: ExamPaperDuties[]): DutyTally[] {
  const names = new Map<string, string>();
  for (const paper of papers) for (const duty of paper.duties) names.set(duty.teacherId, duty.teacherName);

  const out: DutyTally[] = [];
  for (const [teacherId, teacherName] of names) {
    const sessions = sessionDutiesFor(teacherId, papers);
    out.push({
      teacherId,
      teacherName,
      duties: sessions.length,
      minutes: sessions.reduce((n, s) => n + s.minutes, 0),
      keyTime: sessions.filter((s) => s.roles.includes('keyTime')).length,
    });
  }
  return out.sort((a, b) => b.duties - a.duties || a.teacherName.localeCompare(b.teacherName));
}

export type StaffingProblem =
  /** Nobody is invigilating this paper at all. */
  | { kind: 'unstaffed'; paperId: string }
  /** The only invigilator teaches the subject being sat. The school allows a
   *  subject teacher in the room and refuses to let them be alone in it. */
  | { kind: 'soleSubjectTeacher'; paperId: string; teacherId: string; teacherName: string }
  /** On duty somewhere else at the same moment. */
  | { kind: 'doubleBooked'; paperId: string; teacherId: string; teacherName: string; otherPaperId: string };

/**
 * Everything wrong with one session's staffing.
 *
 * `teachesPaper` answers "does this teacher teach the subject this paper is
 * in" — the caller knows, because it holds the timetable and the subject map,
 * and passing the answer in keeps this module free of both.
 */
export function staffingProblems(
  papers: ExamPaperDuties[],
  teachesPaper: (teacherId: string, paper: ExamPaperDuties) => boolean,
): StaffingProblem[] {
  const out: StaffingProblem[] = [];

  for (const paper of papers) {
    const invigilators = paper.duties.filter((d) => d.role === 'invigilator');
    if (!invigilators.length) out.push({ kind: 'unstaffed', paperId: paper.id });
    else if (invigilators.length === 1 && teachesPaper(invigilators[0].teacherId, paper)) {
      out.push({
        kind: 'soleSubjectTeacher',
        paperId: paper.id,
        teacherId: invigilators[0].teacherId,
        teacherName: invigilators[0].teacherName,
      });
    }
  }

  /* Double-booked: the same person owed in two places at once.
     PAPERS IN ONE SESSION RUN IN ONE ROOM (rule 3), so being named on both is
     the normal way the office staffs a session and is never a clash — the
     morning of 30 September has one person on a paper and on another's key
     time, minutes apart, entirely deliberately. Only an overlap ACROSS
     sessions counts, which is a key-time window running into the next one. */
  const byTeacher = new Map<string, { paper: ExamPaperDuties; duty: ExamDuty }[]>();
  for (const paper of papers) {
    for (const duty of paper.duties) {
      const list = byTeacher.get(duty.teacherId) ?? [];
      list.push({ paper, duty });
      byTeacher.set(duty.teacherId, list);
    }
  }
  for (const [teacherId, list] of byTeacher) {
    for (let i = 0; i < list.length; i += 1) {
      for (let j = i + 1; j < list.length; j += 1) {
        const a = list[i]; const b = list[j];
        if (a.paper.date !== b.paper.date) continue;
        if (a.paper.session === b.paper.session) continue;
        if (!overlaps(a.duty.from, a.duty.to, b.duty.from, b.duty.to)) continue;
        out.push({
          kind: 'doubleBooked',
          paperId: a.paper.id,
          teacherId,
          teacherName: a.duty.teacherName,
          otherPaperId: b.paper.id,
        });
      }
    }
  }
  return out;
}

/** Papers grouped into the sessions the school runs, in order. */
export function papersBySession(papers: ExamPaperDuties[]): { key: string; date: string; session: string; papers: ExamPaperDuties[] }[] {
  const by = new Map<string, { key: string; date: string; session: string; papers: ExamPaperDuties[] }>();
  for (const paper of papers) {
    const key = `${paper.date}|${paper.session}`;
    const row = by.get(key) ?? { key, date: paper.date, session: paper.session, papers: [] };
    row.papers.push(paper);
    by.set(key, row);
  }
  return [...by.values()]
    .map((row) => ({ ...row, papers: [...row.papers].sort((a, b) => a.start.localeCompare(b.start) || a.code.localeCompare(b.code)) }))
    .sort((a, b) => a.date.localeCompare(b.date) || a.session.localeCompare(b.session));
}

/* ── which part of the school is sitting it ───────────────────────── */

/**
 * The phase a set of classes belongs to, or null if they are not all one.
 *
 * Null is a real answer and not a failure: an empty list means nobody has
 * said who sits it, and a MIXED list means the answer is "both". Either way
 * the honest thing is to rule nobody out, which is what null does downstream.
 */
export function phaseOfClasses(classNames: Iterable<string>): SchoolPhase | null {
  let primary = false;
  let secondary = false;
  for (const name of classNames) {
    if (!String(name ?? '').trim()) continue;
    if (isSecondaryClassName(String(name))) secondary = true;
    else primary = true;
    if (primary && secondary) return null;
  }
  if (secondary) return 'secondary';
  if (primary) return 'primary';
  return null;
}

/** A Cambridge syllabus/component code, as the office writes it: '0625/41'. */
const CAMBRIDGE_CODE = /^\d{4}\/\d{2}$/;

/**
 * Which part of the school sits this paper.
 *
 * TWO sources, in order, and neither of them invented:
 *
 *   1. THE CLASSES THE SERIES SAYS SIT IT. `ExamSeries.classes[]` is the
 *      office's own statement — "Form 5A, AS, A2" — and a class name is
 *      exactly what `isSecondaryClassName` reads. This is the answer whenever
 *      it is available, and it is the one that will still be right when the
 *      school's own primary exams become a second `kind` of series.
 *   2. THE CODE. A Cambridge syllabus code IS a secondary qualification —
 *      there is no such thing as an IGCSE sat by Std 3. So a paper carrying
 *      one is secondary even inside a series that also covers primary.
 *
 * Anything else returns null, meaning "not known", and nobody is ruled out.
 * Guessing here would quietly hide half the staff from the exam officer.
 */
export function phaseOfPaper(
  paper: Pick<ExamPaperDuties, 'code'>,
  sittingClasses: Iterable<string> = [],
): SchoolPhase | null {
  const fromClasses = phaseOfClasses(sittingClasses);
  if (fromClasses) return fromClasses;
  return CAMBRIDGE_CODE.test(String(paper.code ?? '').trim()) ? 'secondary' : null;
}

/* ── who is free to take a duty ───────────────────────────────────── */

/**
 * Why one teacher is, or is not, free for a duty in a given window.
 *
 * Reasons rather than a boolean, for the reason `clashesFor` gives them:
 * "Mr Mazorodze teaches Form 3B Physics, periods 2 and 3" is what lets the
 * exam officer decide, and "unavailable" is what makes them override it
 * without reading.
 *
 * `ownSubject` is deliberately NOT an availability reason. The school's rule
 * (2026-09-18) is that a teacher may invigilate their own subject's paper and
 * may never be the only invigilator in the room — so it is a thing to know
 * when choosing, not a thing that rules them out.
 */
export interface DutyAvailability {
  teacherId: string;
  /** Already named on this paper. They cannot be added to it twice. */
  alreadyOn: boolean;
  /**
   * Owed in another room at an overlapping moment.
   *
   * ONLY ACROSS SESSIONS. Papers in one session run concurrently in the same
   * room (rule 3), so a teacher named on two of them is standing in one room
   * once — which is how the office staffs a session, not a clash.
   */
  elsewhere: { paper: ExamPaperDuties; duty: ExamDuty } | null;
  /** Lessons this window would empty. */
  lessons: DutyClash[];
  /** They teach the subject being sat. Allowed, but never alone. */
  ownSubject: boolean;
  /**
   * They work in the OTHER part of the school (the school, 2026-09-23).
   *
   * Only ever true when BOTH are known — the paper's phase and the teacher's.
   * A teacher the school has recorded nothing about is not ruled out by
   * silence: live, all 74 carry `divisions[]`, but a new starter on their
   * first day would not, and hiding them would look identical to hiding them
   * on purpose.
   */
  wrongPhase: boolean;
}

/**
 * Free means: not already on it, not owed elsewhere, teaching nothing, and in
 * the right part of the school.
 */
export function isFree(a: DutyAvailability): boolean {
  return !a.alreadyOn && !a.elsewhere && !a.lessons.length && !a.wrongPhase;
}

/**
 * `phase` is optional, and leaving it out filters on phase not at all.
 *
 * That is the honest default rather than a convenience: a caller that has not
 * worked out which part of the school sits the paper has no business ruling
 * anybody out on it.
 */
export function availabilityFor(
  teacherId: string,
  paper: ExamPaperDuties,
  window: { from: string; to: string },
  papers: ExamPaperDuties[],
  slots: TimetableSlot[],
  times: PeriodTimes[],
  teachesPaper: (teacherId: string, paper: ExamPaperDuties) => boolean,
  phase?: {
    /** Which part of the school sits this paper; null = not known. */
    paper: SchoolPhase | null;
    /** The phases this teacher works in; empty = not known. */
    teaches: ReadonlySet<SchoolPhase> | undefined;
  },
): DutyAvailability {
  let elsewhere: { paper: ExamPaperDuties; duty: ExamDuty } | null = null;
  for (const other of papers) {
    if (other.date !== paper.date) continue;
    if (other.session === paper.session) continue;
    for (const duty of other.duties) {
      if (duty.teacherId !== teacherId) continue;
      if (!overlaps(window.from, window.to, duty.from, duty.to)) continue;
      elsewhere = { paper: other, duty };
      break;
    }
    if (elsewhere) break;
  }

  const teaches = phase?.teaches;
  const wrongPhase = !!phase?.paper && !!teaches && teaches.size > 0 && !teaches.has(phase.paper);

  return {
    teacherId,
    alreadyOn: paper.duties.some((d) => d.teacherId === teacherId),
    elsewhere,
    lessons: clashesFor(teacherId, { date: paper.date, from: window.from, to: window.to }, slots, times),
    ownSubject: teachesPaper(teacherId, paper),
    wrongPhase,
  };
}

/**
 * A paper moved to new times (the school, 2026-10-06: edited from its card on
 * Exams → Timetable). Three things move, and one does not:
 *   - the paper's own start and end;
 *   - every FCS (key-time) duty, to the FCS window given, when one is given;
 *   - an invigilator whose window WAS the paper's, which moves with the paper.
 * An invigilator on a split shift ("09:30–11:00") keeps their hours, because
 * those were set by hand and a guess at how to stretch them would be wrong.
 */
export function retimePaper(
  paper: ExamPaperDuties,
  times: { start: string; end: string },
  fcs?: { from: string; to: string } | null,
): ExamPaperDuties {
  const duties = paper.duties.map((d) => {
    if (d.role === 'keyTime') return fcs ? { ...d, from: fcs.from, to: fcs.to } : d;
    if (d.from === paper.start && d.to === paper.end) return { ...d, from: times.start, to: times.end };
    return d;
  });
  return { ...paper, start: times.start, end: times.end, duties };
}

/** Everyone on duty reports this long before they start (the school,
 *  2026-10-06: "15 minutes before the actual start of the exam"). */
export const REPORT_BEFORE_MINUTES = 15;

/**
 * When a person must REPORT for a duty: fifteen minutes before it starts.
 * An invigilator for the whole paper therefore reports 15 minutes before the
 * paper, an FCS supervisor 15 minutes before their key-time window, and an
 * invigilator on a later split shift 15 minutes before their shift.
 */
export function reportTime(from: string, before = REPORT_BEFORE_MINUTES): string {
  const m = minutesOf(from);
  if (Number.isNaN(m)) return '';
  const t = Math.max(0, m - before);
  return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
}

/** The FCS window on a paper, if anyone holds one: the earliest start to the
 *  latest end of its key-time duties. */
export function fcsWindow(paper: ExamPaperDuties): { from: string; to: string; names: string[] } | null {
  const keys = paper.duties.filter((d) => d.role === 'keyTime');
  if (!keys.length) return null;
  const from = keys.map((d) => d.from).filter(Boolean).sort()[0] ?? '';
  const to = keys.map((d) => d.to).filter(Boolean).sort().reverse()[0] ?? '';
  return { from, to, names: keys.map((d) => d.teacherName) };
}

/** One teacher's own duties, in the order they will work them. */
export function dutiesFor(teacherId: string, papers: ExamPaperDuties[]): { paper: ExamPaperDuties; duty: ExamDuty }[] {
  const out: { paper: ExamPaperDuties; duty: ExamDuty }[] = [];
  for (const paper of papers) {
    for (const duty of paper.duties) if (duty.teacherId === teacherId) out.push({ paper, duty });
  }
  return out.sort((a, b) => a.paper.date.localeCompare(b.paper.date) || a.duty.from.localeCompare(b.duty.from));
}
